import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import useInventoryLocations from '@/common/hooks/useInventoryLocations/useInventoryLocations'
import useLocations from '@/common/hooks/useLocations/useLocations'
import usePackaging from '@/common/hooks/usePackaging/usePackaging'
import useRegions from '@/common/hooks/useRegions/useRegions'
import { locationTypes } from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/Locations/LocationsConfiguration/constants'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { getLocationPayload, getMethodsUsedInLocation, getUsedInventoryLocationIds } from './helpers'

const useLocation = ({ id, storeId, selectedLocation }) => {
  const { inventoryLocations, fetchingInventoryLocations } = useInventoryLocations({ storeId })
  const { packaging, fetchingPackaging } = usePackaging({ storeId })
  const { locations } = useLocations({ storeId })
  const { regions, loading: fetchingRegions } = useRegions()
  const { fetchData: createLocation, loading: creatingLocation } = useFetch(configurationServices.createLocation, {
    doInitialRequest: false,
  })
  const { fetchData: editLocation, loading: editingLocation } = useFetch(configurationServices.editLocation, {
    doInitialRequest: false,
  })
  const { data: deliveryMethods, loading: gettingDeliveryMethods } = useFetch(
    () => configurationServices.getStoreLogisticsPreferences({ storeId }),
    {
      doInitialRequest: selectedLocation?.delivery_method_ids?.length > 0,
    }
  )
  const { data: shippingMethods, loading: gettingShippingMethods } = useFetch(
    () => configurationServices.getShippingMethods({ storeId }),
    {
      doInitialRequest: selectedLocation?.shipping_method_ids?.length > 0,
    }
  )
  const [hasChanges, setHasChanges] = useState(false)
  const [type, setType] = useState(selectedLocation?.type || locationTypes.warehouse)
  const [internalName, setInternalName] = useState(selectedLocation?.internal_name || '')
  const [publicName, setPublicName] = useState(selectedLocation?.public_name || '')
  const [line1, setLine1] = useState(selectedLocation?.address?.line_1 || '')
  const [line2, setLine2] = useState(selectedLocation?.address?.line_2 || '')
  const [houseNumber, setHouseNumber] = useState(selectedLocation?.address?.house_number || '')
  const [country, setCountry] = useState(selectedLocation?.address?.country?.code || '')
  const [provinces, setProvinces] = useState([])
  const [province, setProvince] = useState(selectedLocation?.address?.province?.code || '')
  const [locality, setLocality] = useState(selectedLocation?.address?.locality || '')
  const [postalCode, setPostalCode] = useState(selectedLocation?.address?.postcode || '')
  const [phone, setPhone] = useState(selectedLocation?.address?.phone?.number || '')
  const [phonePrefix, setPhonePrefix] = useState(selectedLocation?.address?.phone?.prefix || '')
  const [inventoryLocationId, setInventoryLocationId] = useState(selectedLocation?.inventory_location_id || '')
  const [defaultPackagingId, setDefaultPackagingId] = useState(selectedLocation?.default_packaging_id || '')
  const navigate = useNavigate()

  useEffect(() => {
    if (regions?.length && country) {
      setProvinces(regions.find(r => r.code === country)?.provinces)
    }
  }, [country, id, province, regions, selectedLocation?.address?.province?.code])

  const onChangeCountry = countryCode => {
    setHasChanges(true)
    setCountry(countryCode)
    setProvince('')
    const countryPhonePrefix = regions?.find(r => r.code === countryCode)?.phone_prefix
    setPhonePrefix(countryPhonePrefix || '')
  }

  const isFormValid = () => {
    const requiredFields = [internalName, publicName, line1, country, locality, postalCode, phone, phonePrefix]

    if (provinces?.length) {
      requiredFields.push(province)
    }

    return requiredFields.every(field => field)
  }

  const onSaveChanges = async () => {
    const payload = getLocationPayload({
      type,
      internalName,
      publicName,
      line1,
      line2,
      houseNumber,
      country,
      province,
      locality,
      postalCode,
      phone,
      phonePrefix,
      inventoryLocationId,
      defaultPackagingId,
    })

    if (id === 'new' || id === 'duplicate') {
      await createLocation({ storeId, payload })
      navigate(routes.configurationPage.logistics.locations)
    } else {
      await editLocation({ storeId, locationId: selectedLocation.id, payload })
      setHasChanges(false)
    }
  }

  const revertChanges = () => {
    setType(selectedLocation?.type || locationTypes.store)
    setInternalName(selectedLocation?.internal_name || '')
    setPublicName(selectedLocation?.public_name || '')
    setLine1(selectedLocation?.address?.line_1 || '')
    setLine2(selectedLocation?.address?.line_2 || '')
    setCountry(selectedLocation?.address?.country?.code || '')
    setProvince(selectedLocation?.address?.province?.code || '')
    setLocality(selectedLocation?.address?.locality || '')
    setPostalCode(selectedLocation?.address?.postcode || '')
    setPhone(selectedLocation?.address?.phone?.number || '')
    setPhonePrefix(selectedLocation?.address?.phone?.prefix || '')
    setInventoryLocationId(selectedLocation?.inventory_location_id || '')
    setDefaultPackagingId(selectedLocation?.default_packaging_id || '')
    setHasChanges(false)
  }

  return {
    type,
    setType: type => {
      setHasChanges(true)
      setType(type)
    },
    hasChanges,
    setHasChanges,
    regions,
    fetchingRegions,
    onChangeCountry,
    country,
    provinces,
    province,
    setProvince: provinceCode => {
      setHasChanges(true)
      setProvince(provinceCode)
    },
    internalName,
    setInternalName: name => {
      setHasChanges(true)
      setInternalName(name)
    },
    publicName,
    setPublicName: name => {
      setHasChanges(true)
      setPublicName(name)
    },
    line1,
    setLine1: line => {
      setHasChanges(true)
      setLine1(line)
    },
    line2,
    setLine2: line => {
      setHasChanges(true)
      setLine2(line)
    },
    houseNumber,
    setHouseNumber: number => {
      setHasChanges(true)
      setHouseNumber(number)
    },
    locality,
    setLocality: locality => {
      setHasChanges(true)
      setLocality(locality)
    },
    postalCode,
    setPostalCode: postalCode => {
      setHasChanges(true)
      setPostalCode(postalCode)
    },
    inventoryLocationId,
    setInventoryLocationId: id => {
      setHasChanges(true)
      setInventoryLocationId(id)
    },
    isFormValid,
    onSaveChanges,
    creatingLocation,
    revertChanges,
    editingLocation,
    deliveryMethods: getMethodsUsedInLocation(deliveryMethods?.delivery_methods, selectedLocation?.delivery_method_ids),
    gettingDeliveryMethods,
    phone,
    setPhone: number => {
      setHasChanges(true)
      setPhone(number)
    },
    phonePrefix,
    setPhonePrefix: prefix => {
      setHasChanges(true)
      setPhonePrefix(prefix)
    },
    shippingMethods: getMethodsUsedInLocation(shippingMethods, selectedLocation?.shipping_method_ids),
    gettingShippingMethods,
    inventoryLocations,
    fetchingInventoryLocations,
    packaging,
    fetchingPackaging,
    defaultPackagingId,
    setDefaultPackagingId: id => {
      setHasChanges(true)
      setDefaultPackagingId(id)
    },
    usedInventoryLocationIds: getUsedInventoryLocationIds(locations, selectedLocation?.id),
  }
}

export default useLocation
